import React, {useState, useEffect, useContext } from 'react';
import axios from '../Common/axios';
import {Button, Input, Select, Table, DatePicker, message} from "antd";
import {getGridTableHeight, dateFormatter} from '../Common/util';
import AppContext from '../../components/AppContext';
import Cookies from 'js-cookie';
import TopMenu from '../Common/TopMenu';

const { RangePicker } = DatePicker;

const ReportsList = () => {
    const myContext = useContext(AppContext);
    
    // 쿠키에서 인증 토큰 값 가져오기
    const authToken = Cookies.get('access_token');
    const authenticated = authToken ? true : false;

    // 변수 선언
    const [companyCode, setCompanyCode] = useState(myContext.companyCode);
    const [dataSource, setDataSource] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });

    // 검색 조건
    const [searchParams, setSearchParams] = useState({
        reportType: '',
        reportName: '',
        startDate: '',
        endDate: ''
    });

    // 컬럼 정의
    const columns = [
        {
            title: 'No',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            align: 'center',
        },
        {
            title: '보고서 유형',
            dataIndex: 'report_type_display',
            key: 'report_type_display',
            width: 150,
            align: 'center',
        },
        {
            title: '보고서명',
            dataIndex: 'report_name',
            key: 'report_name',
            width: 300,
        },
        {
            title: '파일명',
            dataIndex: 'file_name',
            key: 'file_name',
            width: 250,
            render: (text, record) => (
                <a 
                    href="#" 
                    onClick={(e) => {
                        e.preventDefault();
                        handleDownload(record);
                    }}
                    style={{ color: '#1890ff', textDecoration: 'underline' }}
                >
                    {text}
                </a>
            ),
        },
        {
            title: '파일크기',
            dataIndex: 'file_size_mb',
            key: 'file_size_mb',
            width: 100,
            align: 'right',
            render: (text) => `${text} MB`,
        },
        {
            title: '생성일시',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            align: 'center',
            render: (text) => dateFormatter(text, 'YYYY-MM-DD HH:mm'),
        },
        {
            title: '다운로드 횟수',
            dataIndex: 'download_count',
            key: 'download_count',
            width: 120,
            align: 'center',
        },
        {
            title: '최근 다운로드',
            dataIndex: 'last_downloaded_at',
            key: 'last_downloaded_at',
            width: 150,
            align: 'center',
            render: (text) => text ? dateFormatter(text, 'YYYY-MM-DD HH:mm') : '-',
        },
    ];

    // 데이터 조회
    const fetchReports = async (page = 1, pageSize = 20) => {
        setIsLoading(true);
        try {
            // 빈 값 제거하여 파라미터 구성
            const params = {
                page: page,
                page_size: pageSize,
            };
            
            // 검색 조건이 있는 경우만 추가 (빈 문자열은 제외)
            if (searchParams.reportType && searchParams.reportType.trim() !== '') {
                params.report_type = searchParams.reportType;
            }
            if (searchParams.reportName && searchParams.reportName.trim() !== '') {
                params.report_name = searchParams.reportName;
            }
            if (searchParams.startDate && searchParams.startDate.trim() !== '') {
                params.start_date = searchParams.startDate;
            }
            if (searchParams.endDate && searchParams.endDate.trim() !== '') {
                params.end_date = searchParams.endDate;
            }

            const response = await axios.get('/table/filter/generated-reports/', {
                params: params
            });

            if (response.data.status === 'success') {
                setDataSource(response.data.data.reports);
                setPagination({
                    current: response.data.data.pagination.page,
                    pageSize: response.data.data.pagination.page_size,
                    total: response.data.data.pagination.total_count
                });
            }
        } catch (error) {
            console.error('보고서 목록 조회 실패:', error);
            message.error('보고서 목록을 불러오는데 실패했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 다운로드 처리
    const handleDownload = async (record) => {
        try {
            const response = await axios.get(`/table/filter/generated-reports/${record.id}/download/`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', record.file_name);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            message.success('파일이 다운로드되었습니다.');
            
            // 다운로드 후 목록 새로고침
            fetchReports(pagination.current, pagination.pageSize);
        } catch (error) {
            console.error('다운로드 실패:', error);
            message.error('파일 다운로드에 실패했습니다.');
        }
    };

    // 검색
    const handleSearch = () => {
        fetchReports(1, pagination.pageSize);
    };

    // 초기화
    const handleReset = () => {
        setSearchParams({
            reportType: '',
            reportName: '',
            startDate: '',
            endDate: ''
        });
    };

    // 페이지 변경
    const handleTableChange = (paginationInfo) => {
        fetchReports(paginationInfo.current, paginationInfo.pageSize);
    };

    // 날짜 변경 처리
    const handleDateChange = (dates, dateStrings) => {
        setSearchParams(prev => ({
            ...prev,
            startDate: dateStrings[0],
            endDate: dateStrings[1]
        }));
    };

    // 초기 데이터 로드
    useEffect(() => {
        if (authenticated) {
            fetchReports();
        }
    }, [authenticated]);

    // 검색 조건 변경 시 자동 검색 (값이 있을 때만)
    useEffect(() => {
        if (authenticated && (searchParams.reportType || searchParams.reportName)) {
            const timer = setTimeout(() => {
                fetchReports(1, pagination.pageSize);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [searchParams.reportType, searchParams.reportName]);

    return (
        <div>
            <TopMenu selectKey={'reports'} />                
                <div className="section">
                 <table className='search'>
                    <colgroup>
                        <col style={{width:'8%'}} />
                        <col style={{width:'18%'}} />
                        <col style={{width:'8%'}} />
                        <col style={{width:'18%'}} />
                        <col style={{width:'8%'}} />
                        <col style={{width:'18%'}} />
                    </colgroup>
                    <tbody>
                    <tr>
                        <th>보고서 유형</th>
                        <td>
                           <Select
                                style={{ width: 200, margin:0, top:-4 }}
                                placeholder="보고서 유형 선택"
                                value={searchParams.reportType}
                                onChange={(value) => setSearchParams(prev => ({...prev, reportType: value}))}
                                allowClear
                            >
                                <Select.Option value="vulnerability">취약점분석</Select.Option>
                                <Select.Option value="detailed_vulnerability">상세취약점분석</Select.Option>
                                <Select.Option value="risk_assessment">위험평가</Select.Option>
                                <Select.Option value="risk_management">위험관리</Select.Option>
                                <Select.Option value="followup_assessment">이행점검</Select.Option>    
                                <Select.Option value="vulnerability_comparison">비교분석</Select.Option>
                                <Select.Option value="consolidated_vulnerability">통합취약점분석</Select.Option>
                                <Select.Option value="consolidated_risk_assessment">통합위험평가</Select.Option>
                                <Select.Option value="consolidated_risk_management">통합위험관리</Select.Option>
                            </Select>
                        </td>
                        <th>보고서명</th>
                        <td>
                            <Input
                                style={{ width: 300 }}
                                placeholder="보고서명 검색"
                                value={searchParams.reportName}
                                onChange={(e) => setSearchParams(prev => ({...prev, reportName: e.target.value}))}
                            />
                        </td>
                        <th>생성일자</th>
                        <td>
                            <RangePicker
                                style={{ width: 250 }}
                                onChange={handleDateChange}
                            />
                        </td>
                    </tr>
                    </tbody>
                </table>
                <div className="search_button">
                        <Button type="primary" onClick={handleSearch} style={{ marginRight: '8px' }}> 검색 </Button>
                        <Button onClick={handleReset}> 초기화 </Button>
                </div>
{/* 

                    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px' }}>보고서 유형:</label>
                            <Select
                                style={{ width: 200 }}
                                placeholder="보고서 유형 선택"
                                value={searchParams.reportType}
                                onChange={(value) => setSearchParams(prev => ({...prev, reportType: value}))}
                                allowClear
                            >
                                <Select.Option value="vulnerability">취약점분석</Select.Option>
                                <Select.Option value="detailed_vulnerability">상세취약점분석</Select.Option>
                                <Select.Option value="risk_assessment">위험평가</Select.Option>
                                <Select.Option value="risk_management">위험관리</Select.Option>
                                <Select.Option value="followup_assessment">이행점검</Select.Option>    
                                <Select.Option value="vulnerability_comparison">비교분석</Select.Option>
                                <Select.Option value="consolidated_vulnerability">통합취약점분석</Select.Option>
                                <Select.Option value="consolidated_risk_assessment">통합위험평가</Select.Option>
                                <Select.Option value="consolidated_risk_management">통합위험관리</Select.Option>
                            </Select>
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px' }}>보고서명:</label>
                            <Input
                                style={{ width: 300 }}
                                placeholder="보고서명 검색"
                                value={searchParams.reportName}
                                onChange={(e) => setSearchParams(prev => ({...prev, reportName: e.target.value}))}
                            />
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px' }}>생성일자:</label>
                            <RangePicker
                                style={{ width: 250 }}
                                onChange={handleDateChange}
                            />
                        </div>

                        <Button type="primary" onClick={handleSearch} style={{ marginRight: '8px' }}>
                            검색
                        </Button>
                        <Button onClick={handleReset}>
                            초기화
                        </Button>
                    </div>
                 </div> */}

                {/* 테이블 */}
                <Table
                    style={{ marginBottom: '15px' }}
                    columns={columns}
                    dataSource={dataSource}
                    loading={isLoading}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        position: ['bottomCenter'],
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={handleTableChange}
                    scroll={{ y: getGridTableHeight() }}
                    size="small"
                />
            </div>
        </div>
    );
};

export default ReportsList;